import { ActionToolbarGroup } from "../action-toolbar";
import { MDSModal } from "@/mds/components/overlays/modal/modal";
import { IImageLightboxImage, IImageLightboxProps } from "./image-lightbox.types";
import { StyledLightboxImage } from "./image-lightbox.styles";
import {
  StyledLightboxContainer,
  StyledLightboxToolbar,
  StyledZoomLabel,
} from "../shared-lightbox.styles";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components/buttons";
import { MDSIconKind } from "@/mds/components/icon";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  WheelEvent as ReactWheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { clamp } from "lodash-es";
import { modNumber } from "@/modules/number/modulo";
import { downloadImageFromUrl } from "../media-download";
import {
  ZOOM_STEP,
  MIN_ZOOM,
  MAX_ZOOM,
  DEFAULT_ZOOM,
  WHEEL_ZOOM_SENSITIVITY,
  TRACKPAD_PINCH_ZOOM_SENSITIVITY,
  PINCH_ZOOM_EXPONENT,
  LINE_HEIGHT_PX,
  PAGE_HEIGHT_PX,
  ZOOM_EPSILON,
  DEFAULT_OFFSET,
  Point,
} from "../lightbox.constants";
import { useHotkey } from "@tanstack/react-hotkeys";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";

type PinchSnapshot = {
  startDistance: number;
  startZoom: number;
  baseCenter: Point;
  anchorLocal: Point;
};

type TouchListLike = {
  [index: number]: {
    clientX: number;
    clientY: number;
  };
  length: number;
};

const normalizeWheelDelta = (event: { deltaY: number; deltaMode: number }) => {
  switch (event.deltaMode) {
    case 1:
      return event.deltaY * LINE_HEIGHT_PX;
    case 2:
      return event.deltaY * PAGE_HEIGHT_PX;
    default:
      return event.deltaY;
  }
};

const isPointInsideRect = (point: Point, rect: DOMRect) =>
  point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;

const getTouchDistance = (touches: TouchListLike) => {
  const [firstTouch, secondTouch] = [touches[0], touches[1]];
  return Math.hypot(
    secondTouch.clientX - firstTouch.clientX,
    secondTouch.clientY - firstTouch.clientY
  );
};

const getTouchMidpoint = (touches: TouchListLike) => {
  const [firstTouch, secondTouch] = [touches[0], touches[1]];
  return {
    x: (firstTouch.clientX + secondTouch.clientX) / 2,
    y: (firstTouch.clientY + secondTouch.clientY) / 2,
  };
};

const hasTouchInputSupport = () => {
  if (typeof window === "undefined") return false;

  const hasTouchPoints = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
  const hasCoarsePointer =
    typeof window.matchMedia === "function" && window.matchMedia("(any-pointer: coarse)").matches;

  return hasTouchPoints || hasCoarsePointer;
};

export const ImageLightbox = ({
  src,
  alt = "",
  images,
  initialIndex = 0,
  isOpen,
  isDismissable = true,
  handleClose,
  onOpenChange,
  zIndex,
  context,
}: IImageLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [offset, setOffset] = useState<Point>(DEFAULT_OFFSET);
  const [isMousePanning, setIsMousePanning] = useState(false);
  const [isTouchInteracting, setIsTouchInteracting] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const zoomRef = useRef(DEFAULT_ZOOM);
  const offsetRef = useRef<Point>(DEFAULT_OFFSET);

  const pinchSnapshotRef = useRef<PinchSnapshot | null>(null);
  const isPinchZoomingRef = useRef(false);

  const isMousePanningRef = useRef(false);
  const mousePanStartPointRef = useRef<Point>(DEFAULT_OFFSET);
  const mousePanStartOffsetRef = useRef<Point>(DEFAULT_OFFSET);

  const isTouchPanningRef = useRef(false);
  const touchPanStartPointRef = useRef<Point>(DEFAULT_OFFSET);
  const touchPanStartOffsetRef = useRef<Point>(DEFAULT_OFFSET);

  const isTouchInput = hasTouchInputSupport();

  const resolvedImages = useMemo<IImageLightboxImage[]>(() => {
    if (images && images.length > 0) return images;
    if (src) return [{ src, alt }];
    return [];
  }, [images, src, alt]);

  const currentImage = resolvedImages[currentIndex];
  const hasMultipleImages = resolvedImages.length > 1;
  const isPannable = zoom > DEFAULT_ZOOM + ZOOM_EPSILON;

  const commitTransform = useCallback((nextZoom: number, nextOffset: Point) => {
    zoomRef.current = nextZoom;
    offsetRef.current = nextOffset;
    setZoom(nextZoom);
    setOffset(nextOffset);
  }, []);

  const applyZoomAtPoint = useCallback(
    (targetZoom: number, point: Point, fallbackToImageCenterWhenOutside: boolean): boolean => {
      if (!imageRef.current) return false;

      const previousZoom = zoomRef.current;
      const nextZoom = clamp(targetZoom, MIN_ZOOM, MAX_ZOOM);
      if (Math.abs(nextZoom - previousZoom) < ZOOM_EPSILON) return false;

      const rect = imageRef.current.getBoundingClientRect();
      const center = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      const anchorPoint = isPointInsideRect(point, rect)
        ? point
        : fallbackToImageCenterWhenOutside
          ? center
          : null;
      if (!anchorPoint) return false;

      const previousOffset = offsetRef.current;
      const scaleRatio = nextZoom / previousZoom;

      const nextCenter = {
        x: anchorPoint.x - (anchorPoint.x - center.x) * scaleRatio,
        y: anchorPoint.y - (anchorPoint.y - center.y) * scaleRatio,
      };

      let nextOffset = {
        x: previousOffset.x + (nextCenter.x - center.x),
        y: previousOffset.y + (nextCenter.y - center.y),
      };

      if (nextZoom <= DEFAULT_ZOOM + ZOOM_EPSILON) {
        nextOffset = DEFAULT_OFFSET;
        isMousePanningRef.current = false;
        isTouchPanningRef.current = false;
        setIsMousePanning(false);
      }

      commitTransform(nextZoom, nextOffset);
      return true;
    },
    [commitTransform]
  );

  const applyZoomAroundImageCenter = useCallback(
    (targetZoom: number) => {
      if (!imageRef.current) {
        const nextZoom = clamp(targetZoom, MIN_ZOOM, MAX_ZOOM);
        const nextOffset =
          nextZoom <= DEFAULT_ZOOM + ZOOM_EPSILON ? DEFAULT_OFFSET : offsetRef.current;
        commitTransform(nextZoom, nextOffset);
        return;
      }

      const rect = imageRef.current.getBoundingClientRect();
      void applyZoomAtPoint(
        targetZoom,
        { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
        false
      );
    },
    [applyZoomAtPoint, commitTransform]
  );

  // Analytics: session tracking
  const sessionStartRef = useRef<number>(0);
  const prevIsOpenRef = useRef(false);
  const contextRef = useRef(context);
  contextRef.current = context;

  // Reset state when modal opens or images change
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoom(DEFAULT_ZOOM);
      setOffset(DEFAULT_OFFSET);
      setIsMousePanning(false);
      setIsTouchInteracting(false);

      zoomRef.current = DEFAULT_ZOOM;
      offsetRef.current = DEFAULT_OFFSET;
      pinchSnapshotRef.current = null;
      isPinchZoomingRef.current = false;

      isMousePanningRef.current = false;
      mousePanStartPointRef.current = DEFAULT_OFFSET;
      mousePanStartOffsetRef.current = DEFAULT_OFFSET;
      isTouchPanningRef.current = false;
      touchPanStartPointRef.current = DEFAULT_OFFSET;
      touchPanStartOffsetRef.current = DEFAULT_OFFSET;

      // focus the container for the sake of useHotkey
      requestAnimationFrame(() => {
        containerRef.current?.focus();
      });

      // Analytics: track open and start session
      sessionStartRef.current = Date.now();
      if (context) {
        trackEvent(TrackedEvent.ImageViewerOpen, { context });
      }
    }
  }, [isOpen, initialIndex, context]);

  // Analytics: track close when lightbox is dismissed
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen && contextRef.current) {
      trackEvent(TrackedEvent.ImageViewerClose, {
        context: contextRef.current,
        session_duration_ms: Date.now() - sessionStartRef.current,
      });
    }
    prevIsOpenRef.current = !!isOpen;
  }, [isOpen]);

  useEffect(() => {
    const handleWindowMouseMove = (event: MouseEvent) => {
      if (!isMousePanningRef.current) return;

      event.preventDefault();

      const nextOffset = {
        x: mousePanStartOffsetRef.current.x + (event.clientX - mousePanStartPointRef.current.x),
        y: mousePanStartOffsetRef.current.y + (event.clientY - mousePanStartPointRef.current.y),
      };

      offsetRef.current = nextOffset;
      setOffset(nextOffset);
    };

    const handleWindowMouseUp = () => {
      if (!isMousePanningRef.current) return;
      isMousePanningRef.current = false;
      setIsMousePanning(false);
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const containerElement = containerRef.current;

    const isInsideLightbox = (target: EventTarget | null) =>
      target instanceof Node && containerElement.contains(target);

    // Prevent browser/page zoom and scroll while interacting inside the lightbox.
    const handleWheel = (event: WheelEvent) => {
      if (!isInsideLightbox(event.target)) return;
      event.preventDefault();
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isInsideLightbox(event.target)) return;
      if (event.touches.length >= 2) {
        event.preventDefault();
      }
    };

    const handleGestureEvent = (event: Event) => {
      if (!isInsideLightbox(event.target)) return;
      isPinchZoomingRef.current = event.type === "gesturestart" || event.type === "gesturechange";
      event.preventDefault();
    };

    document.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false, capture: true });
    document.addEventListener("gesturestart", handleGestureEvent, {
      passive: false,
      capture: true,
    });
    document.addEventListener("gesturechange", handleGestureEvent, {
      passive: false,
      capture: true,
    });
    document.addEventListener("gestureend", handleGestureEvent, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
      document.removeEventListener("touchmove", handleTouchMove, { capture: true });
      document.removeEventListener("gesturestart", handleGestureEvent, { capture: true });
      document.removeEventListener("gesturechange", handleGestureEvent, { capture: true });
      document.removeEventListener("gestureend", handleGestureEvent, { capture: true });
    };
  }, [isOpen]);

  const handleContainerClick = useCallback(() => {
    if (!isDismissable || !handleClose) return;
    handleClose();
  }, [handleClose, isDismissable]);

  const handleImageClick = useCallback((event: ReactMouseEvent<HTMLImageElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleImageMouseDown = useCallback((event: ReactMouseEvent<HTMLImageElement>) => {
    if (event.button !== 0 || zoomRef.current <= DEFAULT_ZOOM + ZOOM_EPSILON) return;

    event.preventDefault();
    event.stopPropagation();

    isMousePanningRef.current = true;
    setIsMousePanning(true);
    mousePanStartPointRef.current = { x: event.clientX, y: event.clientY };
    mousePanStartOffsetRef.current = offsetRef.current;
  }, []);

  const handleToolbarClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleToolbarWheel = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleZoomIn = useCallback(() => {
    applyZoomAroundImageCenter(zoomRef.current + ZOOM_STEP);
  }, [applyZoomAroundImageCenter]);

  const handleZoomOut = useCallback(() => {
    applyZoomAroundImageCenter(zoomRef.current - ZOOM_STEP);
  }, [applyZoomAroundImageCenter]);

  const handleWheelZoom = useCallback(
    (event: ReactWheelEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (isPinchZoomingRef.current) return;
      if (hasTouchInputSupport()) return;

      const normalizedDelta = normalizeWheelDelta(event);
      if (normalizedDelta === 0) return;

      const zoomSensitivity = event.ctrlKey
        ? TRACKPAD_PINCH_ZOOM_SENSITIVITY
        : WHEEL_ZOOM_SENSITIVITY;
      const zoomFactor = Math.exp(-normalizedDelta * zoomSensitivity);
      void applyZoomAtPoint(
        zoomRef.current * zoomFactor,
        { x: event.clientX, y: event.clientY },
        true
      );
    },
    [applyZoomAtPoint]
  );

  const handleTouchStart = useCallback((event: ReactTouchEvent<HTMLImageElement>) => {
    if (event.touches.length === 2) {
      event.preventDefault();
      event.stopPropagation();
      const startDistance = getTouchDistance(event.touches);
      const midpoint = getTouchMidpoint(event.touches);
      if (!imageRef.current || startDistance === 0) {
        pinchSnapshotRef.current = null;
        isPinchZoomingRef.current = false;
        setIsTouchInteracting(false);
        return;
      }

      const rect = imageRef.current.getBoundingClientRect();
      if (!isPointInsideRect(midpoint, rect)) {
        pinchSnapshotRef.current = null;
        isPinchZoomingRef.current = false;
        setIsTouchInteracting(false);
        return;
      }

      const startOffset = offsetRef.current;
      const startZoom = zoomRef.current;
      const rectCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      const baseCenter = {
        x: rectCenter.x - startOffset.x,
        y: rectCenter.y - startOffset.y,
      };

      pinchSnapshotRef.current = {
        startDistance,
        startZoom,
        baseCenter,
        anchorLocal: {
          x: (midpoint.x - (baseCenter.x + startOffset.x)) / startZoom,
          y: (midpoint.y - (baseCenter.y + startOffset.y)) / startZoom,
        },
      };
      isPinchZoomingRef.current = true;
      setIsTouchInteracting(true);
      isTouchPanningRef.current = false;
      return;
    }

    if (event.touches.length === 1 && zoomRef.current > DEFAULT_ZOOM + ZOOM_EPSILON) {
      event.preventDefault();
      event.stopPropagation();
      const touch = event.touches[0];
      isTouchPanningRef.current = true;
      touchPanStartPointRef.current = { x: touch.clientX, y: touch.clientY };
      touchPanStartOffsetRef.current = offsetRef.current;
      isPinchZoomingRef.current = false;
      pinchSnapshotRef.current = null;
      setIsTouchInteracting(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (event: ReactTouchEvent<HTMLImageElement>) => {
      if (event.touches.length === 2 && pinchSnapshotRef.current) {
        event.preventDefault();
        event.stopPropagation();

        const currentDistance = getTouchDistance(event.touches);
        if (currentDistance === 0 || pinchSnapshotRef.current.startDistance === 0) return;

        const rawScaleFactor = currentDistance / pinchSnapshotRef.current.startDistance;
        const scaleFactor = Math.pow(rawScaleFactor, PINCH_ZOOM_EXPONENT);
        const midpoint = getTouchMidpoint(event.touches);
        const nextZoom = clamp(
          pinchSnapshotRef.current.startZoom * scaleFactor,
          MIN_ZOOM,
          MAX_ZOOM
        );
        let nextOffset = {
          x:
            midpoint.x -
            pinchSnapshotRef.current.baseCenter.x -
            pinchSnapshotRef.current.anchorLocal.x * nextZoom,
          y:
            midpoint.y -
            pinchSnapshotRef.current.baseCenter.y -
            pinchSnapshotRef.current.anchorLocal.y * nextZoom,
        };

        if (nextZoom <= DEFAULT_ZOOM + ZOOM_EPSILON) {
          nextOffset = DEFAULT_OFFSET;
          isTouchPanningRef.current = false;
          setIsMousePanning(false);
        }

        commitTransform(nextZoom, nextOffset);
        return;
      }

      if (
        event.touches.length === 1 &&
        isTouchPanningRef.current &&
        zoomRef.current > DEFAULT_ZOOM + ZOOM_EPSILON
      ) {
        event.preventDefault();
        event.stopPropagation();
        const touch = event.touches[0];
        const nextOffset = {
          x: touchPanStartOffsetRef.current.x + (touch.clientX - touchPanStartPointRef.current.x),
          y: touchPanStartOffsetRef.current.y + (touch.clientY - touchPanStartPointRef.current.y),
        };
        offsetRef.current = nextOffset;
        setOffset(nextOffset);
      }
    },
    [commitTransform]
  );

  const handleTouchEnd = useCallback((event: ReactTouchEvent<HTMLImageElement>) => {
    if (event.touches.length < 2) {
      pinchSnapshotRef.current = null;
      isPinchZoomingRef.current = false;
    }

    if (event.touches.length === 1 && zoomRef.current > DEFAULT_ZOOM + ZOOM_EPSILON) {
      const touch = event.touches[0];
      isTouchPanningRef.current = true;
      touchPanStartPointRef.current = { x: touch.clientX, y: touch.clientY };
      touchPanStartOffsetRef.current = offsetRef.current;
      setIsTouchInteracting(true);
      return;
    }

    isTouchPanningRef.current = false;
    setIsTouchInteracting(false);
  }, []);

  const handleTouchCancel = useCallback(() => {
    pinchSnapshotRef.current = null;
    isTouchPanningRef.current = false;
    isPinchZoomingRef.current = false;
    setIsTouchInteracting(false);
  }, []);

  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => {
      if (resolvedImages.length === 0) return prev;
      return modNumber({ numberA: prev - 1, numberB: resolvedImages.length });
    });
  }, [resolvedImages.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => {
      if (resolvedImages.length === 0) return prev;
      return modNumber({ numberA: prev + 1, numberB: resolvedImages.length });
    });
  }, [resolvedImages.length]);

  const handleDownload = useCallback(async () => {
    if (!currentImage) return;

    await downloadImageFromUrl({
      src: currentImage.src,
      fileName: currentImage.alt || "image",
    });
  }, [currentImage]);

  const hotkeyOptions = { enabled: isOpen };
  const galleryHotkeyOptions = { enabled: isOpen && hasMultipleImages };

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape_to_close),
    event => {
      if (!isDismissable || !handleClose) return;
      event.preventDefault();
      event.stopPropagation();
      handleClose();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.viewer_previous),
    handlePrevious,
    galleryHotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.viewer_next),
    handleNext,
    galleryHotkeyOptions
  );

  useHotkey(resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.viewer_zoom_in), handleZoomIn, hotkeyOptions);

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.viewer_zoom_in_alternate),
    handleZoomIn,
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.viewer_zoom_out),
    handleZoomOut,
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.viewer_zoom_out_alternate),
    handleZoomOut,
    hotkeyOptions
  );

  if (resolvedImages.length === 0 || !currentImage) {
    return null;
  }

  const zoomPercent = `${Math.round(zoom * 100)}%`;

  return (
    <MDSModal
      isOpen={isOpen}
      isDismissable={isDismissable}
      handleClose={handleClose}
      onOpenChange={onOpenChange}
      zIndex={zIndex}
    >
      <StyledLightboxContainer
        ref={containerRef}
        tabIndex={-1}
        onClick={handleContainerClick}
        onWheel={isTouchInput ? undefined : handleWheelZoom}
      >
        <StyledLightboxImage
          ref={imageRef}
          src={currentImage.src}
          alt={currentImage.alt || ""}
          zoom={zoom}
          offsetX={offset.x}
          offsetY={offset.y}
          isPannable={isPannable}
          isPanning={isMousePanning}
          isTouchInteracting={isTouchInteracting}
          draggable={false}
          onClick={handleImageClick}
          onMouseDown={handleImageMouseDown}
          onWheel={isTouchInput ? undefined : handleWheelZoom}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        />

        <StyledLightboxToolbar
          groups={
            [
              {
                key: "zoom-controls",
                items: [
                  {
                    key: "zoom-out",
                    node: (
                      <MDSTooltip
                        config={getShortcutTooltipConfig({
                          action: "viewer_zoom_out",
                          label: "Zoom out",
                          placement: MDSTooltipPlacement.Top,
                        })}
                      >
                        <MDSIconButton
                          aria-label="Zoom out"
                          iconKind={MDSIconKind.Minus}
                          variant={MDSIconButtonVariant.Default}
                          size={MDSIconButtonSize.Small}
                          isDisabled={zoom <= MIN_ZOOM}
                          onClick={handleZoomOut}
                        />
                      </MDSTooltip>
                    ),
                  },
                  {
                    key: "zoom-label",
                    node: (
                      <StyledZoomLabel>
                        <MDSText
                          variant={MDSTextVariant.ParagraphSm}
                          weight={MDSTextWeight.Medium}
                          color={MDSTextColor.Contrast}
                        >
                          {zoomPercent}
                        </MDSText>
                      </StyledZoomLabel>
                    ),
                  },
                  {
                    key: "zoom-in",
                    node: (
                      <MDSTooltip
                        config={getShortcutTooltipConfig({
                          action: "viewer_zoom_in",
                          label: "Zoom in",
                          placement: MDSTooltipPlacement.Top,
                        })}
                      >
                        <MDSIconButton
                          aria-label="Zoom in"
                          iconKind={MDSIconKind.Plus}
                          variant={MDSIconButtonVariant.Default}
                          size={MDSIconButtonSize.Small}
                          isDisabled={zoom >= MAX_ZOOM}
                          onClick={handleZoomIn}
                        />
                      </MDSTooltip>
                    ),
                  },
                ],
              },
              {
                key: "download",
                items: [
                  {
                    key: "download-image",
                    node: (
                      <MDSTooltip
                        config={{
                          label: "Download file",
                          placement: MDSTooltipPlacement.Top,
                        }}
                      >
                        <MDSIconButton
                          aria-label="Download image"
                          iconKind={MDSIconKind.ArrowDownToLine}
                          variant={MDSIconButtonVariant.Default}
                          size={MDSIconButtonSize.Small}
                          onClick={handleDownload}
                        />
                      </MDSTooltip>
                    ),
                  },
                ],
              },
              ...(hasMultipleImages
                ? [
                    {
                      key: "navigation",
                      items: [
                        {
                          key: "previous-image",
                          node: (
                            <MDSTooltip
                              config={getShortcutTooltipConfig({
                                action: "viewer_previous",
                                label: "Previous image",
                                placement: MDSTooltipPlacement.Top,
                              })}
                            >
                              <MDSIconButton
                                aria-label="Previous image"
                                iconKind={MDSIconKind.LeftArrow}
                                variant={MDSIconButtonVariant.Default}
                                size={MDSIconButtonSize.Small}
                                onClick={handlePrevious}
                              />
                            </MDSTooltip>
                          ),
                        },
                        {
                          key: "next-image",
                          node: (
                            <MDSTooltip
                              config={getShortcutTooltipConfig({
                                action: "viewer_next",
                                label: "Next image",
                                placement: MDSTooltipPlacement.Top,
                              })}
                            >
                              <MDSIconButton
                                aria-label="Next image"
                                iconKind={MDSIconKind.RightArrow}
                                variant={MDSIconButtonVariant.Default}
                                size={MDSIconButtonSize.Small}
                                onClick={handleNext}
                              />
                            </MDSTooltip>
                          ),
                        },
                      ],
                    },
                  ]
                : []),
              {
                key: "close",
                items: [
                  {
                    key: "close-image-preview",
                    node: (
                      <MDSTooltip
                        config={getShortcutTooltipConfig({
                          action: "escape_to_close",
                          label: "Close",
                          placement: MDSTooltipPlacement.Top,
                        })}
                      >
                        <MDSIconButton
                          aria-label="Close image preview"
                          iconKind={MDSIconKind.Exit}
                          variant={MDSIconButtonVariant.Default}
                          size={MDSIconButtonSize.Small}
                          onClick={handleClose}
                        />
                      </MDSTooltip>
                    ),
                  },
                ],
              },
            ] satisfies ActionToolbarGroup[]
          }
          onClick={handleToolbarClick}
          onWheel={handleToolbarWheel}
        />
      </StyledLightboxContainer>
    </MDSModal>
  );
};
